import React, { useState, useEffect, useCallback, useMemo } from 'react';
import Navbar from '../Navbar/Navbar';
import './AdminPanel.css';
import * as XLSX from 'xlsx';

const AdminPanel = ({ onNavigate, onLogout, user }) => {
  const [activeTab, setActiveTab] = useState('personal');
  const [fichajesTab, setFichajesTab] = useState('HL'); // Nueva pestaña para HL/HLE
  const [filtroEmpleado, setFiltroEmpleado] = useState('');
  const [fechaDesde, setFechaDesde] = useState(() => new Date().toISOString().slice(0, 10));
  const [fechaHasta, setFechaHasta] = useState('');
  const [filaExpandida, setFilaExpandida] = useState(null);
  const [empleados, setEmpleados] = useState([]);
  const [fichajes, setFichajes] = useState([]);
  const [detalleFichajes, setDetalleFichajes] = useState({});
  const [solicitudes, setSolicitudes] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [usuarioEditando, setUsuarioEditando] = useState(null);
  const [nuevoUsuario, setNuevoUsuario] = useState({
    usuario: '',
    nombre: '',
    apellidos: '',
    telefono: '',
    password: '',
    horario_inicio: '01:00:00',
    horario_fin: '23:59:00',
    typeUser: 0
  });
  const [estadisticas, setEstadisticas] = useState(null);
  const [usuarioSeleccionado, setUsuarioSeleccionado] = useState('');
  const [fechaDesdeEstadisticas, setFechaDesdeEstadisticas] = useState(() => {
    return new Date().getFullYear() + '-01-01';
  });
  const [fechaHastaEstadisticas, setFechaHastaEstadisticas] = useState(() => new Date().toISOString().slice(0, 10));
  const [tablaEstadisticas, setTablaEstadisticas] = useState('HL');
  const [cargandoEstadisticas, setCargandoEstadisticas] = useState(false);

  // ── Estado Ausencias ──────────────────────────────────────────────────────
  const [ausencias, setAusencias] = useState([]);
  const [ausenciaFiltroUsuario, setAusenciaFiltroUsuario] = useState('');
  const [ausenciaEditando, setAusenciaEditando] = useState(null);
  const [ausenciaForm, setAusenciaForm] = useState({
    usuario: '', fecha_inicio: '', fecha_fin: '',
    tipo: 'vacacion', motivo: '',
    parcial: false, hora_inicio: '', hora_fin: ''
  });
  const [ausenciaGuardando, setAusenciaGuardando] = useState(false);

  //hay unas variables de entorno que son el archivo .env y el .env.production
  //en el archivo .env.production se encuentran las variables de entorno para producción
  //en el archivo .env se encuentran las variables de entorno para desarrollo
  //esto setea la ruta del servidor dependiendo de si estamos en producción o desarrollo
  const API_URL = process.env.REACT_APP_API_URL || '';

  const cargarEstadoEmpleados = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/estado-personal`);
      const data = await res.json();
      if (data.success) {
        // Ordenar empleados: primero los que no han fichado (fichado = 0)
        const empleadosOrdenados = data.data.sort((a, b) => {
          if (a.fichado === b.fichado) {
            // Si tienen el mismo estado de fichaje, ordenar por nombre
            return a.nombre_completo.localeCompare(b.nombre_completo);
          }
          // Ordenar por estado de fichaje (0 primero, 1 después)
          return a.fichado - b.fichado;
        });
        setEmpleados(empleadosOrdenados);
      }
    } catch (err) {
      console.error('Error al cargar estado de empleados:', err);
    }
  }, [API_URL]);

  const cargarFichajes = useCallback(async () => {
    try {
      // Enviar typeUser para que el servidor devuelva el formato correcto
      const typeUser = user?.typeUser || 0;
      const res = await fetch(`${API_URL}/api/fichajes?typeUser=${typeUser}`);
      const data = await res.json();
      if (data.success) setFichajes(data.data);
    } catch (err) {
      console.error('Error al cargar fichajes:', err);
    }
  }, [API_URL, user]);

  const cargarDetalleFichajes = async (usuario, fecha) => {
    try {
      const fechaCorta = fecha.slice(0, 10);
      // Incluir la tabla (HL o HLE) en la key para diferenciar
      const tabla = esAdmin && fichajesEsObjeto ? fichajesTab : 'HL';
      const key = `${usuario}_${fechaCorta}_${tabla}`;
      if (detalleFichajes[key]) return;

      const res = await fetch(`${API_URL}/api/fichajes-detalle?usuario=${usuario}&fecha=${fechaCorta}&tabla=${tabla}`);
      const data = await res.json();
      if (data.success) {
        setDetalleFichajes(prev => ({ ...prev, [key]: data.data }));
      }
    } catch (err) {
      console.error('Error al cargar detalle de fichajes:', err);
    }
  };

  const cargarSolicitudes = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/solicitudes-todas`);
      const data = await res.json();
      if (data.success) setSolicitudes(data.data);
    } catch (err) {
      console.error('Error al cargar solicitudes:', err);
    }
  }, [API_URL]);

  const [mostrarInactivos, setMostrarInactivos] = useState(false);

  const cargarUsuarios = useCallback(async () => {
    try {
      const qs = mostrarInactivos ? '?incluirInactivos=true' : '';
      const res = await fetch(`${API_URL}/api/usuarios${qs}`);
      const data = await res.json();
      if (data.success) setUsuarios(data.data);
    } catch (err) {
      console.error('Error al cargar usuarios:', err);
    }
  }, [API_URL, mostrarInactivos]);

  const usuariosActivos = useMemo(
    () => usuarios.filter(u => u.activo !== 0),
    [usuarios]
  );

  const cargarEstadisticas = useCallback(async () => {
    if (!usuarioSeleccionado) {
      setEstadisticas(null);
      return;
    }

    setCargandoEstadisticas(true);
    try {
      const params = new URLSearchParams({
        usuario: usuarioSeleccionado,
        tabla: tablaEstadisticas
      });

      if (fechaDesdeEstadisticas) {
        params.append('fechaDesde', fechaDesdeEstadisticas);
      }

      if (fechaHastaEstadisticas) {
        params.append('fechaHasta', fechaHastaEstadisticas);
      }

      const url = `${API_URL}/api/estadisticas?${params.toString()}`;
      const res = await fetch(url);
      const data = await res.json();
      if (data.success) {
        setEstadisticas(data.data);
      } else {
        console.warn('[STATS] success=false, message:', data.message);
        setEstadisticas(null);
      }
    } catch (err) {
      console.error('[STATS] fetch error:', err);
      setEstadisticas(null);
    } finally {
      setCargandoEstadisticas(false);
    }
  }, [API_URL, usuarioSeleccionado, fechaDesdeEstadisticas, fechaHastaEstadisticas, tablaEstadisticas]);

  const cargarAusencias = useCallback(async (usuarioFiltro = '') => {
    try {
      const params = usuarioFiltro ? `?usuario=${encodeURIComponent(usuarioFiltro)}` : '';
      const res = await fetch(`${API_URL}/api/ausencias${params}`);
      const data = await res.json();
      if (data.success) setAusencias(data.data);
    } catch (err) {
      console.error('Error al cargar ausencias:', err);
    }
  }, [API_URL]);

  useEffect(() => {
    if (activeTab === 'personal') cargarEstadoEmpleados();
    if (activeTab === 'fichajes') cargarFichajes();
    if (activeTab === 'solicitudes') cargarSolicitudes();
    if (activeTab === 'usuarios') cargarUsuarios();
    if (activeTab === 'estadísticas') cargarUsuarios();
    if (activeTab === 'ausencias') { cargarUsuarios(); cargarAusencias(); }
  }, [activeTab, cargarEstadoEmpleados, cargarFichajes, cargarSolicitudes, cargarUsuarios, cargarAusencias]);

  useEffect(() => {
    if (activeTab === 'estadísticas' && usuarioSeleccionado) {
      cargarEstadisticas();
    }
  }, [activeTab, usuarioSeleccionado, fechaDesdeEstadisticas, fechaHastaEstadisticas, tablaEstadisticas, cargarEstadisticas]);

  // Si estadisticas tiene estructura vieja (sin totales), limpiar y recargar
  useEffect(() => {
    if (estadisticas && !estadisticas.totales && usuarioSeleccionado) {
      console.warn('[STATS] estructura vieja detectada, recargando...');
      setEstadisticas(null);
      cargarEstadisticas();
    }
  }, [estadisticas, usuarioSeleccionado, cargarEstadisticas]);

  // Resetear fila expandida cuando cambia la pestaña HL/HLE
  useEffect(() => {
    setFilaExpandida(null);
  }, [fichajesTab]);

  const actualizarEstadoSolicitud = async (id, estado) => {
    try {
      const res = await fetch(`${API_URL}/api/actualizar-solicitud`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id, estado, autorizadoPor: user?.usuario || 'admin' })
      });
      const data = await res.json();
      if (data.success) cargarSolicitudes();
    } catch (err) {
      console.error('Error al actualizar solicitud:', err);
    }
  };

  // Determinar si el usuario es administrador y los datos vienen en formato {HL: [...], HLE: [...]}
  const esAdmin = user?.typeUser >= 400;
  const fichajesEsObjeto = esAdmin && fichajes && typeof fichajes === 'object' && !Array.isArray(fichajes);
  
  // Obtener los fichajes según la pestaña activa (HL o HLE)
  const fichajesActuales = fichajesEsObjeto 
    ? (fichajes[fichajesTab] || [])
    : (Array.isArray(fichajes) ? fichajes : []);

  const fichajesFiltrados = fichajesActuales.filter((f) => {
    const coincideEmpleado = (f.empleado || f.nombre_completo || '').toLowerCase().includes(filtroEmpleado.toLowerCase());
    const desdeValida = !fechaDesde || f.fecha >= fechaDesde;
    const hastaValida = !fechaHasta || f.fecha <= fechaHasta;
    return coincideEmpleado && desdeValida && hastaValida;
  });

  const solicitudesFiltradas = solicitudes.filter((s) => {
    const coincideEmpleado = s.usuario.toLowerCase().includes(filtroEmpleado.toLowerCase());
    const desdeValida = !fechaDesde || s.fechainicio >= fechaDesde;
    const hastaValida = !fechaHasta || s.fechainicio <= fechaHasta;
    return coincideEmpleado && desdeValida && hastaValida;
  });

  const calcularHorasTrabajadas = (entrada, salida, pausa_inicio = null, pausa_fin = null, comida_inicio = null, comida_fin = null) => {
    if (!entrada || !salida) return '-';
    const [h1, m1] = entrada.split(':').map(Number);
    const [h2, m2] = salida.split(':').map(Number);
    let minutos = (h2 * 60 + m2) - (h1 * 60 + m1);
    
    // Restar tiempo de pausa si existe
    if (pausa_inicio && pausa_fin) {
      const [hp1, mp1] = pausa_inicio.split(':').map(Number);
      const [hp2, mp2] = pausa_fin.split(':').map(Number);
      const minutosPausa = (hp2 * 60 + mp2) - (hp1 * 60 + mp1);
      minutos -= minutosPausa;
    }
    
    // Restar tiempo de comida si existe
    if (comida_inicio && comida_fin) {
      const [hc1, mc1] = comida_inicio.split(':').map(Number);
      const [hc2, mc2] = comida_fin.split(':').map(Number);
      const minutosComida = (hc2 * 60 + mc2) - (hc1 * 60 + mc1);
      minutos -= minutosComida;
    }
    
    const horas = Math.floor(minutos / 60);
    const minutosRest = minutos % 60;
    return `${horas}h ${minutosRest}m`;
  };

  const calcularDiferencia = (entrada, salida) => {
    if (!entrada || !salida) return '-';
    const [h1, m1] = entrada.split(':').map(Number);
    const [h2, m2] = salida.split(':').map(Number);
    const minutos = (h2 * 60 + m2) - (h1 * 60 + m1);
    const diferencia = minutos - 490; // 8h0min = 450min
    const signo = diferencia >= 0 ? '+' : '-';
    const minAbs = Math.abs(diferencia);
    const horas = Math.floor(minAbs / 60);
    const minutosRest = minAbs % 60;
    return `${signo}${horas}h ${minutosRest}m`;
  };

  const calcularTiempoDescanso = (pausa_inicio, pausa_fin) => {
    if (!pausa_inicio || !pausa_fin) return { texto: '-', minutos: 0 };
    const [h1, m1] = pausa_inicio.split(':').map(Number);
    const [h2, m2] = pausa_fin.split(':').map(Number);
    const minutos = (h2 * 60 + m2) - (h1 * 60 + m1);
    const horas = Math.floor(minutos / 60);
    const minutosRest = minutos % 60;
    return {
      texto: `${horas}h ${minutosRest}m`,
      minutos: minutos
    };
  };

  const calcularTiempoComida = (comida_inicio, comida_fin) => {
    if (!comida_inicio || !comida_fin) return { texto: '-', minutos: 0 };
    const [h1, m1] = comida_inicio.split(':').map(Number);
    const [h2, m2] = comida_fin.split(':').map(Number);
    const minutos = (h2 * 60 + m2) - (h1 * 60 + m1);
    const horas = Math.floor(minutos / 60);
    const minutosRest = minutos % 60;
    return {
      texto: `${horas}h ${minutosRest}m`,
      minutos: minutos
    };
  };
  const exportarAExcel = () => {
    const datos = fichajesFiltrados.map(f => ({
      Empleado: f.nombre_completo || f.empleado,
      Fecha: f.fecha,
      Entrada: f.entrada,
      'Pausa Inicio': f.pausa_inicio || '-',
      'Pausa Fin': f.pausa_fin || '-',
      'Comida Inicio': f.comida_inicio || '-',
      'Comida Fin': f.comida_fin || '-',
      Salida: f.salida,
      HorasTrabajadas: calcularHorasTrabajadas(f.entrada, f.salida, f.pausa_inicio, f.pausa_fin, f.comida_inicio, f.comida_fin),
      Diferencia: calcularDiferencia(f.entrada, f.salida),
      'Tiempo de Descanso': calcularTiempoDescanso(f.pausa_inicio, f.pausa_fin).texto,
      'Tiempo de Comida': calcularTiempoComida(f.comida_inicio, f.comida_fin).texto
    }));

    const hoja = XLSX.utils.json_to_sheet(datos);
    const libro = XLSX.utils.book_new();
    const nombreHoja = esAdmin && fichajesEsObjeto ? `Fichajes ${fichajesTab}` : 'Fichajes';
    XLSX.utils.book_append_sheet(libro, hoja, nombreHoja);
    const nombreArchivo = esAdmin && fichajesEsObjeto ? `fichajes_${fichajesTab}.xlsx` : 'fichajes.xlsx';
    XLSX.writeFile(libro, nombreArchivo);
  };

  const exportarEstadisticasAExcel = () => {
    if (!estadisticas?.totales) return;
    const { totales, resumenMensual, detalle, periodo, nombre_completo, usuario } = estadisticas;

    const libro = XLSX.utils.book_new();

    // Hoja 1: Resumen mensual
    const meses = { '01':'Ene','02':'Feb','03':'Mar','04':'Abr','05':'May','06':'Jun',
                    '07':'Jul','08':'Ago','09':'Sep','10':'Oct','11':'Nov','12':'Dic' };
    const resumenDatos = (resumenMensual || []).map(m => {
      const [, mes] = m.mes.split('-');
      return {
        Mes: meses[mes] || m.mes,
        'Días laborables': m.laborables,
        'Ausencias': m.ausencias,
        'Lab. efectivos': m.labEfectivos,
        'Con entrada': m.conEntrada,
        '% Entrada': m.pctEntrada + '%',
        'Con salida': m.conSalida,
        '% Salida': m.pctSalida + '%',
        'Ambas': m.ambas,
        '% Ambas': m.pctAmbas + '%',
        'Tarde llegada': m.tardeEntrada,
        '% Tarde': m.pctTarde + '%',
        'Sin salida': m.sinSalida,
        '% Sin salida': m.pctSinSalida + '%',
        'En oficina': m.enOficina,
        '% Oficina': m.pctOficina + '%',
        'Media horas/día': m.mediaHorasDia,
      };
    });
    const hojaResumen = XLSX.utils.json_to_sheet(resumenDatos);
    XLSX.utils.book_append_sheet(libro, hojaResumen, 'Resumen Mensual');

    // Hoja 2: Totales
    const totalesDatos = [
      { Campo: 'Empleado', Valor: nombre_completo },
      { Campo: 'Usuario', Valor: usuario },
      { Campo: 'Período Desde', Valor: periodo.desde },
      { Campo: 'Período Hasta', Valor: periodo.hasta },
      { Campo: '', Valor: '' },
      { Campo: 'Días laborables', Valor: totales.laborables },
      { Campo: 'Ausencias justificadas', Valor: totales.ausencias },
      { Campo: 'Lab. efectivos', Valor: totales.labEfectivos },
      { Campo: 'Con entrada', Valor: `${totales.conEntrada} (${totales.pctEntrada}%)` },
      { Campo: 'Con salida', Valor: `${totales.conSalida} (${totales.pctSalida}%)` },
      { Campo: 'Ambas (completo)', Valor: `${totales.ambas} (${totales.pctAmbas}%)` },
      { Campo: 'Tarde en entrada', Valor: `${totales.tardeEntrada} (${totales.pctTarde}%)` },
      { Campo: 'Sin salida', Valor: `${totales.sinSalida} (${totales.pctSinSalida}%)` },
      { Campo: 'En oficina', Valor: `${totales.enOficina} (${totales.pctOficina}%)` },
      { Campo: 'Media horas/día', Valor: totales.mediaHorasDia },
    ];
    const hojaTotales = XLSX.utils.json_to_sheet(totalesDatos);
    XLSX.utils.book_append_sheet(libro, hojaTotales, 'Totales');

    // Hoja 3: Detalle día a día
    const detalleDatos = (detalle || []).map(d => ({
      Fecha: d.fecha,
      'Día semana': d.diaSemana || '',
      Estado: d.esAusencia ? ('Ausencia: ' + (d.ausenciaTipo || '')) : (d.esFestivo ? 'Festivo' : (d.sinFichaje ? 'Sin fichaje' : 'OK')),
      Entrada: d.entrada || '-',
      'Ref. entrada': d.entradaRef || '-',
      'Tarde entrada': d.tardeLlegada ? 'Sí' : 'No',
      Salida: d.salida || '-',
      'Ref. salida': d.salidaRef || '-',
      'Salió puntual': d.salidaRegistrada ? (d.salioPuntual ? 'Sí' : 'No') : '-',
      'Min. pausa': d.minutosParPausa > 0 ? d.minutosParPausa : '-',
      'Min. comida': d.minutosParComida > 0 ? d.minutosParComida : '-',
      'Horas netas': d.horasNetas || '-',
      'GPS oficina': d.enOficina === true ? 'Sí' : d.enOficina === false ? 'No' : '-',
    }));
    const hojaDetalle = XLSX.utils.json_to_sheet(detalleDatos);
    XLSX.utils.book_append_sheet(libro, hojaDetalle, 'Detalle');

    const nombreArchivo = `fichajes_${usuario}_${periodo.desde}_${periodo.hasta}.xlsx`;
    XLSX.writeFile(libro, nombreArchivo);
  };

  const exportarEstadisticasAHTML = () => {
    if (!estadisticas?.totales) return;
    const { totales, resumenMensual, detalle, periodo, nombre_completo, usuario, festivosAplicados, ausencias } = estadisticas;
    const mesesNombre = { '01':'Enero','02':'Febrero','03':'Marzo','04':'Abril','05':'Mayo','06':'Junio',
                          '07':'Julio','08':'Agosto','09':'Septiembre','10':'Octubre','11':'Noviembre','12':'Diciembre' };

    const pct = (v) => `<span class="pct">${v}%</span>`;
    const semaforo = (v, umbral70 = 70, umbral90 = 90) => {
      const cls = v >= umbral90 ? 'verde' : v >= umbral70 ? 'naranja' : 'rojo';
      return `<span class="badge ${cls}">${v}%</span>`;
    };

    const filasResumen = (resumenMensual || []).map(m => {
      const [, mes] = m.mes.split('-');
      return `<tr>
        <td><b>${mesesNombre[mes] || m.mes}</b></td>
        <td>${m.laborables}</td>
        <td>${m.ausencias > 0 ? `<span class="badge naranja">${m.ausencias}</span>` : '—'}</td>
        <td>${semaforo(m.pctEntrada)}</td>
        <td>${semaforo(m.pctSalida)}</td>
        <td>${semaforo(m.pctAmbas)}</td>
        <td>${m.tardeEntrada > 0 ? `<span class="badge rojo">${m.tardeEntrada} (${m.pctTarde}%)</span>` : '—'}</td>
        <td>${m.sinSalida > 0 ? `<span class="badge naranja">${m.sinSalida} (${m.pctSinSalida}%)</span>` : '—'}</td>
        <td>${m.enOficina > 0 ? semaforo(m.pctOficina) : '—'}</td>
        <td>${m.mediaHorasDia || '—'}</td>
      </tr>`;
    }).join('');

    const filasDetalle = (detalle || []).map(d => {
      let rowClass = '';
      let estadoCell = '';
      if (d.esAusencia) { rowClass = 'aus'; estadoCell = `<span class="badge verde">${d.ausenciaTipo || 'Ausencia'}</span>`; }
      else if (d.esFestivo) { rowClass = 'fest'; estadoCell = '<span class="badge gris">Festivo</span>'; }
      else if (d.sinFichaje) { rowClass = 'vacio'; estadoCell = '<span class="badge rojo">Sin fichaje</span>'; }
      else if (!d.salida) { rowClass = 'sin-sal'; estadoCell = '<span class="badge naranja">Sin salida</span>'; }
      else if (d.tardeLlegada) { rowClass = 'tarde'; estadoCell = '<span class="badge amarillo">Tarde</span>'; }
      else { estadoCell = '<span class="badge verde">OK</span>'; }

      return `<tr class="${rowClass}">
        <td>${d.fecha}</td>
        <td>${d.diaSemana || ''}</td>
        <td>${estadoCell}</td>
        <td>${d.entrada || '—'}</td>
        <td>${d.entradaRef || '—'}</td>
        <td>${d.tardeLlegada ? '<span class="badge rojo">Sí</span>' : (d.entrada ? '—' : '')}</td>
        <td>${d.salida || '—'}</td>
        <td>${d.salidaRef || '—'}</td>
        <td>${d.salidaRegistrada ? (d.salioPuntual ? '<span class="badge verde">Sí</span>' : '<span class="badge naranja">No</span>') : '—'}</td>
        <td>${d.minutosParPausa > 0 ? d.minutosParPausa + 'm' : '—'}</td>
        <td>${d.minutosParComida > 0 ? d.minutosParComida + 'm' : '—'}</td>
        <td>${d.horasNetas || '—'}</td>
        <td>${d.enOficina === true ? '<span class="badge verde">Sí</span>' : d.enOficina === false ? '<span class="badge naranja">No</span>' : '—'}</td>
      </tr>`;
    }).join('');

    const html = `<!DOCTYPE html>
<html lang="es">
<head>
<meta charset="UTF-8">
<title>Informe Fichajes — ${nombre_completo}</title>
<style>
  *{box-sizing:border-box;margin:0;padding:0}
  body{font-family:Segoe UI,Arial,sans-serif;background:#f4f5fb;color:#333;padding:24px}
  h1{color:#4e58f2;font-size:1.6rem;margin-bottom:4px}
  .sub{color:#666;font-size:.9rem;margin-bottom:20px}
  .cards{display:flex;flex-wrap:wrap;gap:12px;margin-bottom:24px}
  .card{background:#fff;border-radius:10px;padding:14px 20px;min-width:140px;box-shadow:0 1px 4px #0001;text-align:center}
  .card .val{font-size:2rem;font-weight:700;color:#4e58f2}
  .card .lbl{font-size:.75rem;color:#888;margin-top:2px}
  .card.warn .val{color:#e67e22}
  .card.ok .val{color:#27ae60}
  .card.bad .val{color:#e74c3c}
  table{width:100%;border-collapse:collapse;background:#fff;border-radius:10px;overflow:hidden;box-shadow:0 1px 4px #0001;margin-bottom:24px;font-size:.82rem}
  th{background:#4e58f2;color:#fff;padding:8px 10px;text-align:center}
  td{padding:6px 10px;text-align:center;border-bottom:1px solid #eee}
  tr:last-child td{border-bottom:none}
  tr.aus td{background:#f0fff4}
  tr.fest td{background:#f5f5f5;color:#aaa}
  tr.vacio td{background:#fff5f5}
  tr.sin-sal td{background:#fff8f0}
  tr.tarde td{background:#fffff0}
  .badge{display:inline-block;padding:2px 8px;border-radius:12px;font-size:.75rem;font-weight:600}
  .badge.verde{background:#d4edda;color:#155724}
  .badge.rojo{background:#f8d7da;color:#721c24}
  .badge.naranja{background:#fde3cf;color:#7d3c0a}
  .badge.amarillo{background:#fffde7;color:#7d6608}
  .badge.gris{background:#e9ecef;color:#495057}
  .pct{font-weight:600}
  h2{font-size:1.1rem;color:#4e58f2;margin-bottom:10px}
  .festivos{font-size:.78rem;color:#888}
  @media print{body{background:#fff;padding:0}.cards .card{box-shadow:none}}
</style>
</head>
<body>
<h1>Informe de Fichajes</h1>
<div class="sub">${nombre_completo} &nbsp;·&nbsp; ${periodo.desde} → ${periodo.hasta} &nbsp;·&nbsp; Generado ${new Date().toLocaleDateString('es-ES')}</div>

<h2>Resumen global</h2>
<div class="cards">
  <div class="card"><div class="val">${totales.laborables}</div><div class="lbl">Días lab.</div></div>
  <div class="card ${totales.pctEntrada>=90?'ok':totales.pctEntrada>=70?'':' warn'}"><div class="val">${totales.pctEntrada}%</div><div class="lbl">% Entrada</div></div>
  <div class="card ${totales.pctSalida>=90?'ok':totales.pctSalida>=70?'':'warn'}"><div class="val">${totales.pctSalida}%</div><div class="lbl">% Salida</div></div>
  <div class="card ${totales.pctAmbas>=90?'ok':totales.pctAmbas>=70?'':'warn'}"><div class="val">${totales.pctAmbas}%</div><div class="lbl">% Completo</div></div>
  <div class="card ${totales.pctTarde<=10?'ok':totales.pctTarde<=25?'warn':'bad'}"><div class="val">${totales.pctTarde}%</div><div class="lbl">% Tarde</div></div>
  <div class="card ${totales.pctSinSalida<=10?'ok':totales.pctSinSalida<=25?'warn':'bad'}"><div class="val">${totales.pctSinSalida}%</div><div class="lbl">% Sin salida</div></div>
  <div class="card"><div class="val">${totales.pctOficina}%</div><div class="lbl">% En oficina</div></div>
  <div class="card ok"><div class="val">${totales.mediaHorasDia}</div><div class="lbl">Media h/día</div></div>
  ${totales.ausencias > 0 ? `<div class="card warn"><div class="val">${totales.ausencias}</div><div class="lbl">Ausencias</div></div>` : ''}
</div>

<h2>Resumen mensual</h2>
<table>
<thead><tr>
  <th>Mes</th><th>Lab.</th><th>Aus.</th>
  <th>% Entrada</th><th>% Salida</th><th>% Completo</th>
  <th>Tarde</th><th>Sin salida</th><th>% Oficina</th><th>Media h/día</th>
</tr></thead>
<tbody>${filasResumen}</tbody>
</table>

<h2>Detalle día a día</h2>
<table>
<thead><tr>
  <th>Fecha</th><th>Día</th><th>Estado</th>
  <th>Entrada</th><th>Ref.</th><th>Tarde</th>
  <th>Salida</th><th>Ref.</th><th>Puntual</th>
  <th>Pausa</th><th>Comida</th><th>Horas</th><th>Oficina</th>
</tr></thead>
<tbody>${filasDetalle}</tbody>
</table>

${festivosAplicados && festivosAplicados.length > 0 ? `<div class="festivos">Festivos excluidos (Comunidad de Madrid): ${festivosAplicados.join(', ')}</div>` : ''}
</body>
</html>`;

    const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `informe_fichajes_${usuario}_${periodo.desde}_${periodo.hasta}.html`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // ── CRUD Ausencias ────────────────────────────────────────────────────────
  const guardarAusencia = async () => {
    const { usuario, fecha_inicio, fecha_fin, tipo, motivo, parcial, hora_inicio, hora_fin } = ausenciaForm;
    if (!usuario || !fecha_inicio || !fecha_fin || !tipo) return alert('Completa todos los campos obligatorios');
    if (fecha_fin < fecha_inicio) return alert('La fecha fin no puede ser anterior a la fecha inicio');
    if (parcial) {
      if (!hora_inicio || !hora_fin) return alert('Si marcas "Parcial", debes indicar hora_inicio y hora_fin');
      if (hora_inicio >= hora_fin)   return alert('hora_inicio debe ser anterior a hora_fin');
      if (fecha_inicio !== fecha_fin) return alert('Las ausencias parciales solo pueden ser de un mismo día');
    }
    setAusenciaGuardando(true);
    try {
      const body = {
        usuario, fecha_inicio, fecha_fin, tipo, motivo,
        creado_por: user?.usuario || '',
        hora_inicio: parcial ? hora_inicio : null,
        hora_fin:    parcial ? hora_fin    : null,
      };
      let res;
      if (ausenciaEditando) {
        res = await fetch(`${API_URL}/api/ausencias/${ausenciaEditando}`, {
          method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body)
        });
      } else {
        res = await fetch(`${API_URL}/api/ausencias`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body)
        });
      }
      const data = await res.json();
      if (data.success) {
        setAusenciaEditando(null);
        setAusenciaForm({ usuario: '', fecha_inicio: '', fecha_fin: '', tipo: 'vacacion', motivo: '', parcial: false, hora_inicio: '', hora_fin: '' });
        cargarAusencias(ausenciaFiltroUsuario);
      } else {
        alert('Error al guardar: ' + (data.message || 'Error desconocido'));
      }
    } catch (err) {
      alert('Error de red al guardar ausencia');
    } finally {
      setAusenciaGuardando(false);
    }
  };

  const editarAusencia = (a) => {
    setAusenciaEditando(a.id);
    const esParcial = !!(a.hora_inicio && a.hora_fin);
    setAusenciaForm({
      usuario:      a.usuario,
      fecha_inicio: a.fecha_inicio,
      fecha_fin:    a.fecha_fin,
      tipo:         a.tipo,
      motivo:       a.motivo || '',
      parcial:      esParcial,
      hora_inicio:  esParcial ? a.hora_inicio : '',
      hora_fin:     esParcial ? a.hora_fin    : '',
    });
  };

  const eliminarAusencia = async (id) => {
    if (!window.confirm('¿Eliminar esta ausencia?')) return;
    try {
      const res = await fetch(`${API_URL}/api/ausencias/${id}`, { method: 'DELETE' });
      const data = await res.json();
      if (data.success) cargarAusencias(ausenciaFiltroUsuario);
      else alert('Error al eliminar: ' + (data.message || ''));
    } catch (err) {
      alert('Error de red al eliminar');
    }
  };

  const agregarUsuario = async () => {
    try {
      const res = await fetch(`${API_URL}/api/usuarios`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(nuevoUsuario)
      });
      const data = await res.json();
      if (data.success) {
        cargarUsuarios();
        setNuevoUsuario({
          usuario: '',
          nombre: '',
          apellidos: '',
          telefono: '',
          password: '',
          horario_inicio: '01:00:00',
          horario_fin: '23:59:00',
          typeUser: 0
        });
      }
    } catch (err) {
      console.error('Error al agregar usuario:', err);
    }
  };

  const actualizarUsuario = async (id) => {
    try {
      const res = await fetch(`${API_URL}/api/usuarios/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(usuarioEditando)
      });
      const data = await res.json();
      if (data.success) {
        cargarUsuarios();
        setUsuarioEditando(null);
      }
    } catch (err) {
      console.error('Error al actualizar usuario:', err);
    }
  };

  const desactivarUsuario = async (id, nombreCompleto = '') => {
    const aviso = `¿Desactivar a ${nombreCompleto || 'este usuario'}?\n\nSe conservarán sus fichajes y trazabilidad histórica, pero no aparecerá en Personal, Fichajes, Estadísticas activas ni en otros selectores. Podrás reactivarlo cuando quieras desde "Mostrar inactivos".`;
    if (!window.confirm(aviso)) return;
    try {
      const res = await fetch(`${API_URL}/api/usuarios/${id}`, { method: 'DELETE' });
      const data = await res.json();
      if (data.success) cargarUsuarios();
      else alert('Error al desactivar: ' + (data.message || ''));
    } catch (err) {
      console.error('Error al desactivar usuario:', err);
    }
  };

  const reactivarUsuario = async (id) => {
    try {
      const res = await fetch(`${API_URL}/api/usuarios/${id}/activar`, { method: 'PUT' });
      const data = await res.json();
      if (data.success) cargarUsuarios();
      else alert('Error al reactivar: ' + (data.message || ''));
    } catch (err) {
      console.error('Error al reactivar usuario:', err);
    }
  };

  const generarMensajeBienvenida = (usuario) => {
    return `👋 ¡Hola ${usuario.nombre_completo}!

Bienvenido a Remai WorkTime, nuestra plataforma de fichajes diseñada para que registrar tu jornada laboral sea más fácil y organizada. A partir de mañana, deberás fichar tu entrada y salida cada día desde:
👉 remaiworktime.com.es

🔐 Aquí tienes tus datos de acceso:

    Usuario: ${usuario.usuario}

    Contraseña: ${usuario.pas}
    🕒 Tu horario asignado: ${usuario.horario_inicio?.slice(0, 5)} a ${usuario.horario_fin?.slice(0, 5)}

Te recomendamos ingresar hoy mismo para familiarizarte con la plataforma. Si tienes cualquier duda, desde el Departamento de Informática estamos aquí para ayudarte. 🙌

¡Gracias por sumarte con buena energía! Seguimos mejorando juntos. 💪😊`;
  };

  const copiarMensaje = (usuario) => {
    const mensaje = generarMensajeBienvenida(usuario);

    // Función auxiliar para copiar usando execCommand como fallback
    const fallbackCopy = (text) => {
      const textArea = document.createElement("textarea");
      textArea.value = text;

      // Evitar que el elemento cause scroll
      textArea.style.top = "0";
      textArea.style.left = "0";
      textArea.style.position = "fixed";

      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();

      try {
        const successful = document.execCommand('copy');
        if (successful) {
          alert('¡Mensaje copiado al portapapeles!');
        } else {
          alert('No se pudo copiar el mensaje. Inténtalo manualmente.');
        }
      } catch (err) {
        console.error('Fallback: Error al copiar', err);
        alert('No se pudo copiar el mensaje. Inténtalo manualmente.');
      }

      document.body.removeChild(textArea);
    };

    if (!navigator.clipboard) {
      fallbackCopy(mensaje);
      return;
    }

    navigator.clipboard.writeText(mensaje)
      .then(() => {
        alert('¡Mensaje copiado al portapapeles!');
      })
      .catch(err => {
        console.error('Error al copiar:', err);
        fallbackCopy(mensaje);
      });
  };

  return (
    <div className="admin-container">
      <Navbar active="admin" onNavigate={onNavigate} onLogout={onLogout} user={user} />
      <main className="admin-main">
        <h2>Portal de Administración</h2>

        <div className="tabs">
          {['Personal', 'Fichajes', 'Solicitudes', 'Estadísticas', 'Usuarios', ...(esAdmin ? ['Ausencias'] : [])].map(tab => (
            <button
              key={tab}
              className={`tab-btn ${activeTab === tab.toLowerCase() ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.toLowerCase())}
            >
              {tab}
            </button>
          ))}
        </div>

        {activeTab === 'personal' && (
          <div className="fichajes-panel">
            <table className="fichajes-table">
              <thead>
                <tr>
                  <th>Empleado</th>
                  <th>Estado</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {empleados.map((emp, i) => (
                  <tr key={i}>
                    <td>{emp.nombre_completo}</td>
                    <td style={{ fontSize: '1.5rem' }}>{emp.fichado ? '🟢' : '🔴'}</td>
                    <td>
                      {!emp.fichado && emp.telefono && (() => {
                        const nombre = emp.nombre_completo?.split(' ')[0] || '';
                        const saludo = nombre ? `Buenos días ${nombre}` : 'Buenos días';
                        const mensaje = `${saludo}, recuerda registrar tu fichaje del día de hoy. ¡Gracias!`;
                        return (
                        <a
                          href={`https://wa.me/+34${emp.telefono.replace(/\D/g, '')}?text=${encodeURIComponent(mensaje)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="whatsapp-link-personal"
                        >
                          <span role="img" aria-label="whatsapp">💬</span> Recordar
                        </a>
                        );
                      })()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {activeTab === 'solicitudes' && (
          <div className="fichajes-panel">
            <div className="toolbar">
              <input type="text" placeholder="Todos los empleados" value={filtroEmpleado} onChange={(e) => setFiltroEmpleado(e.target.value)} />
              <input type="date" value={fechaDesde} onChange={(e) => {
                const nuevaFecha = e.target.value;
                setFechaDesde(nuevaFecha);
                setFechaHasta(nuevaFecha);
              }} />
              <input type="date" value={fechaHasta} onChange={(e) => setFechaHasta(e.target.value)} />
            </div>
            <table className="fichajes-table">
              <thead>
                <tr>
                  <th>Empleado</th>
                  <th>Tipo</th>
                  <th>Fecha solicitud</th>
                  <th>Inicio</th>
                  <th>Fin</th>
                  <th>Motivo</th>
                  <th>Estado</th>
                  <th>Autorizado por</th>
                </tr>
              </thead>
              <tbody>
                {solicitudesFiltradas.map((s, i) => (
                  <tr key={i}>
                    <td>{s.nombre_completo}</td>

                    <td>{s.tipo}</td>
                    <td>{s.fechasolicitud?.slice(0, 10)}</td>
                    <td>{s.fechainicio?.slice(0, 10)}</td>
                    <td>{s.fechafin?.slice(0, 10)}</td>

                    <td>{s.motivo}</td>
                    <td>
                      {s.estado === 0 ? (
                        <>
                          <button className="btn-aprobar" onClick={() => actualizarEstadoSolicitud(s.id, 1)}>Aprobar</button>
                          <button className="btn-rechazar" onClick={() => actualizarEstadoSolicitud(s.id, 2)}>Rechazar</button>
                        </>
                      ) : s.estado === 1 ? 'Aprobado' : 'Rechazado'}
                    </td>
                    <td>{s.autorizadopor || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {activeTab === 'fichajes' && (
          <div className="fichajes-panel">
            {/* Pestañas HL y HLE para administradores */}
            {esAdmin && fichajesEsObjeto && (
              <div className="tabs" style={{ marginBottom: '20px' }}>
                <button
                  className={`tab-btn ${fichajesTab === 'HL' ? 'active' : ''}`}
                  onClick={() => setFichajesTab('HL')}
                >
                  HL
                </button>
                <button
                  className={`tab-btn ${fichajesTab === 'HLE' ? 'active' : ''}`}
                  onClick={() => setFichajesTab('HLE')}
                >
                  HLE
                </button>
              </div>
            )}
            <div className="toolbar">
              <input type="text" placeholder="Todos los empleados" value={filtroEmpleado} onChange={(e) => setFiltroEmpleado(e.target.value)} />
              <input type="date" value={fechaDesde} onChange={(e) => {
                const nuevaFecha = e.target.value;
                setFechaDesde(nuevaFecha);
                setFechaHasta(nuevaFecha);
              }} />
              <input type="date" value={fechaHasta} onChange={(e) => setFechaHasta(e.target.value)} />
              <button className="export-btn" onClick={exportarAExcel}>Exportar Excel</button>
            </div>
            <table className="fichajes-table">
              <thead>
                <tr>
                  <th>Empleado</th>
                  <th>Fecha</th>
                  <th>Entrada</th>
                  <th>Pausa Inicio</th>
                  <th>Pausa Fin</th>
                  <th>Comida Inicio</th>
                  <th>Comida Fin</th>
                  <th>Salida</th>
                  <th>Horas Trabajadas</th>
                  <th>Diferencia</th>
                  <th>Tiempo de Descanso</th>
                  <th>Tiempo de Comida</th>
                </tr>
              </thead>
              <tbody>
                {fichajesFiltrados.map((f, i) => {
                  const fechaCorta = f.fecha;
                  const tabla = esAdmin && fichajesEsObjeto ? fichajesTab : 'HL';
                  const key = `${f.empleado || f.nombre_completo}_${fechaCorta}_${tabla}`;
                  return (
                    <React.Fragment key={i}>
                      <tr
                        className="fila-principal"
                        onClick={() => {
                          setFilaExpandida(filaExpandida === i ? null : i);
                          const empleadoUsuario = f.empleado || f.nombre_completo;
                          if (filaExpandida !== i) cargarDetalleFichajes(empleadoUsuario, fechaCorta);
                        }}
                      >
                        <td>{f.nombre_completo || f.empleado}</td>
                        <td>{fechaCorta}</td>
                        <td>{f.entrada || '-'}</td>
                        <td>{f.pausa_inicio || '-'}</td>
                        <td>{f.pausa_fin || '-'}</td>
                        <td>{f.comida_inicio || '-'}</td>
                        <td>{f.comida_fin || '-'}</td>
                        <td>{f.salida || '-'}</td>
                        <td>{calcularHorasTrabajadas(f.entrada, f.salida, f.pausa_inicio, f.pausa_fin, f.comida_inicio, f.comida_fin)}</td>
                        <td>{calcularDiferencia(f.entrada, f.salida)}</td>
                        <td className={(() => {
                          const descanso = calcularTiempoDescanso(f.pausa_inicio, f.pausa_fin);
                          return descanso.minutos > 15 ? 'tiempo-descanso-excedido' : '';
                        })()}>
                          {calcularTiempoDescanso(f.pausa_inicio, f.pausa_fin).texto}
                        </td>
                        <td>
                          {calcularTiempoComida(f.comida_inicio, f.comida_fin).texto}
                        </td>
                      </tr>
                      {filaExpandida === i && detalleFichajes[key] && (
                        <tr className="fila-expandida">
                          <td colSpan="11">
                            <div className="detalle-fichaje" style={{ overflow: 'auto', maxHeight: '300px' }}>
                              <table style={{ minWidth: '800px', borderCollapse: 'collapse' }}>
                                <thead style={{ position: 'sticky', top: 0, backgroundColor: '#fff', zIndex: 1 }}>
                                  <tr>
                                    <th>ID</th>
                                    <th>Usuario</th>
                                    <th>Fecha</th>
                                    <th>Tipo</th>
                                    <th>Hora</th>
                                    <th>Observaciones</th>
                                    <th>Dirección</th>
                                    <th>GPS</th>
                                    <th>URL</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {detalleFichajes[key].map((d, j) => (
                                    <tr key={j}>
                                      <td>{d.id}</td>
                                      <td>{d.usuario}</td>
                                      <td>{d.fecha}</td>
                                      <td>{d.tipo_fichaje}</td>
                                      <td>{d.hora}</td>
                                      <td>{d.obs}</td>
                                      <td>{d.direccion}</td>
                                      <td>{d.pos_gps}</td>
                                      <td><a href={d.gps_url} target="_blank" rel="noopener noreferrer">Ver mapa</a></td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {activeTab === 'estadísticas' && (
          <div className="fichajes-panel">
            <div className="toolbar">
              <select
                value={usuarioSeleccionado}
                onChange={(e) => setUsuarioSeleccionado(e.target.value)}
                style={{ padding: '10px', border: '1px solid #ccc', borderRadius: '8px', minWidth: '200px' }}
              >
                <option value="">Seleccionar persona...</option>
                {usuariosActivos.map((u) => (
                  <option key={u.id} value={u.usuario}>
                    {u.nombre_completo || `${u.nombre} ${u.apellidos}`}
                  </option>
                ))}
              </select>

              {esAdmin && (
                <select
                  value={tablaEstadisticas}
                  onChange={(e) => setTablaEstadisticas(e.target.value)}
                  style={{ padding: '10px', border: '1px solid #ccc', borderRadius: '8px' }}
                >
                  <option value="HL">HL</option>
                  <option value="HLE">HLE</option>
                </select>
              )}

              <input type="date" value={fechaDesdeEstadisticas}
                onChange={(e) => setFechaDesdeEstadisticas(e.target.value)} />
              <input type="date" value={fechaHastaEstadisticas}
                onChange={(e) => setFechaHastaEstadisticas(e.target.value)} />

              {estadisticas?.totales && (
                <>
                  <button className="export-btn" onClick={exportarEstadisticasAExcel}>
                    📊 Excel
                  </button>
                  <button className="export-btn" onClick={exportarEstadisticasAHTML} style={{ backgroundColor: '#2563eb' }}>
                    🌐 HTML
                  </button>
                </>
              )}
            </div>

            {cargandoEstadisticas && (
              <div style={{ textAlign: 'center', padding: '30px', color: '#4e58f2' }}>
                <p>Cargando estadísticas…</p>
              </div>
            )}

            {!cargandoEstadisticas && estadisticas && estadisticas.totales && (() => {
              const { totales, resumenMensual, detalle, periodo, nombre_completo, ausencias } = estadisticas;
              const mesesNombre = { '01':'Ene','02':'Feb','03':'Mar','04':'Abr','05':'May','06':'Jun',
                                    '07':'Jul','08':'Ago','09':'Sep','10':'Oct','11':'Nov','12':'Dic' };
              const badgeStyle = (color) => ({
                display: 'inline-block', padding: '2px 8px', borderRadius: '12px',
                fontSize: '.78rem', fontWeight: 600,
                background: color === 'green' ? '#d4edda' : color === 'red' ? '#f8d7da'
                          : color === 'orange' ? '#fde3cf' : color === 'yellow' ? '#fffde7' : '#e9ecef',
                color: color === 'green' ? '#155724' : color === 'red' ? '#721c24'
                     : color === 'orange' ? '#7d3c0a' : color === 'yellow' ? '#7d6608' : '#495057',
              });
              const semColor = (v, hi=90, mid=70) => v >= hi ? 'green' : v >= mid ? 'orange' : 'red';
              return (
                <div className="estadisticas-container">
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'16px', flexWrap:'wrap', gap:'10px' }}>
                    <h3 style={{ margin:0, color:'#4e58f2' }}>{nombre_completo} — {periodo.desde} → {periodo.hasta}</h3>
                    <div style={{ display:'flex', gap:'8px' }}>
                      <button className="export-btn" onClick={exportarEstadisticasAExcel}>📊 Excel</button>
                      <button className="export-btn" onClick={exportarEstadisticasAHTML} style={{ backgroundColor:'#2563eb' }}>🌐 HTML</button>
                    </div>
                  </div>

                  {/* Tarjetas de totales */}
                  <div style={{ display:'flex', flexWrap:'wrap', gap:'10px', marginBottom:'24px' }}>
                    {[
                      { lbl:'Días lab.', val: totales.laborables, unit:'', color:'#4e58f2' },
                      { lbl:'% Entrada', val: totales.pctEntrada + '%', unit:'', color: totales.pctEntrada>=90?'#27ae60':totales.pctEntrada>=70?'#e67e22':'#e74c3c' },
                      { lbl:'% Salida',  val: totales.pctSalida  + '%', unit:'', color: totales.pctSalida>=90?'#27ae60':totales.pctSalida>=70?'#e67e22':'#e74c3c' },
                      { lbl:'% Completo',val: totales.pctAmbas   + '%', unit:'', color: totales.pctAmbas>=90?'#27ae60':totales.pctAmbas>=70?'#e67e22':'#e74c3c' },
                      { lbl:'% Tarde',   val: totales.pctTarde   + '%', unit:'', color: totales.pctTarde<=10?'#27ae60':totales.pctTarde<=25?'#e67e22':'#e74c3c' },
                      { lbl:'Sin salida',val: totales.sinSalida,         unit:'', color: totales.pctSinSalida<=10?'#27ae60':totales.pctSinSalida<=25?'#e67e22':'#e74c3c' },
                      { lbl:'% Oficina', val: totales.pctOficina + '%', unit:'', color:'#4e58f2' },
                      { lbl:'Media h/día',val: totales.mediaHorasDia,   unit:'', color:'#4e58f2' },
                      ...(totales.ausencias > 0 ? [{ lbl:'Ausencias', val: totales.ausencias, unit:'', color:'#e67e22' }] : []),
                    ].map((c, i) => (
                      <div key={i} style={{ background:'#fff', borderRadius:'10px', padding:'12px 18px', minWidth:'110px', boxShadow:'0 1px 4px #0001', textAlign:'center' }}>
                        <div style={{ fontSize:'1.8rem', fontWeight:700, color: c.color }}>{c.val}</div>
                        <div style={{ fontSize:'.72rem', color:'#888', marginTop:'2px' }}>{c.lbl}</div>
                      </div>
                    ))}
                  </div>

                  {/* Resumen mensual */}
                  <h4 style={{ color:'#4e58f2', marginBottom:'10px' }}>Por mes</h4>
                  <div style={{ overflowX:'auto', marginBottom:'24px' }}>
                    <table className="fichajes-table" style={{ minWidth:'750px' }}>
                      <thead>
                        <tr>
                          <th>Mes</th><th>Lab.</th><th>Aus.</th>
                          <th>% Entrada</th><th>% Salida</th><th>% Completo</th>
                          <th>Tarde</th><th>Sin salida</th><th>% Oficina</th><th>Media h/día</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(resumenMensual || []).map((m, i) => {
                          const [, mes] = m.mes.split('-');
                          return (
                            <tr key={i}>
                              <td><b>{mesesNombre[mes] || m.mes}</b></td>
                              <td>{m.laborables}</td>
                              <td>{m.ausencias > 0 ? <span style={badgeStyle('orange')}>{m.ausencias}</span> : '—'}</td>
                              <td><span style={badgeStyle(semColor(m.pctEntrada))}>{m.pctEntrada}%</span></td>
                              <td><span style={badgeStyle(semColor(m.pctSalida))}>{m.pctSalida}%</span></td>
                              <td><span style={badgeStyle(semColor(m.pctAmbas))}>{m.pctAmbas}%</span></td>
                              <td>{m.tardeEntrada > 0 ? <span style={badgeStyle('red')}>{m.tardeEntrada} ({m.pctTarde}%)</span> : '—'}</td>
                              <td>{m.sinSalida > 0 ? <span style={badgeStyle('orange')}>{m.sinSalida} ({m.pctSinSalida}%)</span> : '—'}</td>
                              <td>{m.enOficina > 0 ? <span style={badgeStyle(semColor(m.pctOficina))}>{m.pctOficina}%</span> : '—'}</td>
                              <td>{m.mediaHorasDia || '—'}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  {/* Detalle día a día */}
                  <h4 style={{ color:'#4e58f2', marginBottom:'10px' }}>Detalle día a día</h4>
                  <div style={{ maxHeight:'420px', overflowY:'auto' }}>
                    <table className="fichajes-table" style={{ fontSize:'.8rem' }}>
                      <thead>
                        <tr>
                          <th>Fecha</th><th>Día</th><th>Estado</th>
                          <th>Entrada</th><th>Ref.</th><th>Tarde</th>
                          <th>Salida</th><th>Ref.</th><th>Puntual</th>
                          <th>Pausa</th><th>Comida</th><th>Horas</th><th>Oficina</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(detalle || []).map((d, i) => {
                          let bg = 'transparent';
                          let estadoBadge = null;
                          if (d.esAusencia) { bg='#f0fff4'; estadoBadge = <span style={badgeStyle('green')}>{d.ausenciaTipo||'Ausencia'}</span>; }
                          else if (d.esFestivo) { bg='#f5f5f5'; estadoBadge = <span style={badgeStyle('grey')}>Festivo</span>; }
                          else if (d.sinFichaje) { bg='#fff5f5'; estadoBadge = <span style={badgeStyle('red')}>Sin fichaje</span>; }
                          else if (!d.salida) { bg='#fff8f0'; estadoBadge = <span style={badgeStyle('orange')}>Sin salida</span>; }
                          else if (d.tardeLlegada) { bg='#fffff0'; estadoBadge = <span style={badgeStyle('yellow')}>Tarde</span>; }
                          else { estadoBadge = <span style={badgeStyle('green')}>OK</span>; }
                          return (
                            <tr key={i} style={{ background: bg }}>
                              <td>{d.fecha}</td>
                              <td>{d.diaSemana || ''}</td>
                              <td>{estadoBadge}</td>
                              <td>{d.entrada || '—'}</td>
                              <td style={{ color:'#888', fontSize:'.72rem' }}>{d.entradaRef || ''}</td>
                              <td>{d.tardeLlegada ? <span style={badgeStyle('red')}>Sí</span> : (d.entrada ? '—' : '')}</td>
                              <td>{d.salida || '—'}</td>
                              <td style={{ color:'#888', fontSize:'.72rem' }}>{d.salidaRef || ''}</td>
                              <td>{d.salidaRegistrada ? (d.salioPuntual ? <span style={badgeStyle('green')}>Sí</span> : <span style={badgeStyle('orange')}>No</span>) : '—'}</td>
                              <td>{d.minutosParPausa > 0 ? d.minutosParPausa + 'm' : '—'}</td>
                              <td>{d.minutosParComida > 0 ? d.minutosParComida + 'm' : '—'}</td>
                              <td><b>{d.horasNetas || '—'}</b></td>
                              <td>{d.enOficina === true ? <span style={badgeStyle('green')}>Sí</span> : d.enOficina === false ? <span style={badgeStyle('orange')}>No</span> : '—'}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            })()}

            {!cargandoEstadisticas && !estadisticas && usuarioSeleccionado && (
              <div style={{ textAlign:'center', padding:'20px', color:'#666' }}>
                <p>No se encontraron estadísticas para el período seleccionado.</p>
              </div>
            )}

            {!usuarioSeleccionado && (
              <div style={{ textAlign:'center', padding:'40px', color:'#666' }}>
                <p>Selecciona una persona para ver sus estadísticas.</p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'usuarios' && (
          <div className="usuarios-panel">
            <div className="admin-new-user-form">
              <h3>Agregar Nuevo Usuario</h3>

              <div className="admin-form-group">
                <label htmlFor="nuevo-nombre" className="admin-form-label">Nombre</label>
                <input
                  id="nuevo-nombre"
                  className="admin-form-input"
                  type="text"
                  value={nuevoUsuario.nombre}
                  onChange={(e) => setNuevoUsuario({ ...nuevoUsuario, nombre: e.target.value })}
                  autoComplete="off"
                />
              </div>

              <div className="admin-form-group">
                <label htmlFor="nuevo-apellidos" className="admin-form-label">Apellidos</label>
                <input
                  id="nuevo-apellidos"
                  className="admin-form-input"
                  type="text"
                  value={nuevoUsuario.apellidos}
                  onChange={(e) => setNuevoUsuario({ ...nuevoUsuario, apellidos: e.target.value })}
                  autoComplete="off"
                />
              </div>

              <div className="admin-form-group">
                <label htmlFor="nuevo-telefono" className="admin-form-label">Teléfono</label>
                <input
                  id="nuevo-telefono"
                  className="admin-form-input"
                  type="tel"
                  value={nuevoUsuario.telefono}
                  onChange={(e) => setNuevoUsuario({ ...nuevoUsuario, telefono: e.target.value })}
                  autoComplete="off"
                />
              </div>

              <div className="admin-form-group">
                <label htmlFor="nuevo-horario-inicio" className="admin-form-label">Horario Inicio</label>
                <input
                  id="nuevo-horario-inicio"
                  className="admin-form-input"
                  type="time"
                  value={nuevoUsuario.horario_inicio}
                  onChange={(e) => setNuevoUsuario({ ...nuevoUsuario, horario_inicio: e.target.value })}
                />
              </div>

              <div className="admin-form-group">
                <label htmlFor="nuevo-horario-fin" className="admin-form-label">Horario Fin</label>
                <input
                  id="nuevo-horario-fin"
                  className="admin-form-input"
                  type="time"
                  value={nuevoUsuario.horario_fin}
                  onChange={(e) => setNuevoUsuario({ ...nuevoUsuario, horario_fin: e.target.value })}
                />
              </div>

              <div className="admin-form-group">
                <label htmlFor="nuevo-tipo-usuario" className="admin-form-label">Tipo de Usuario</label>
                <select
                  id="nuevo-tipo-usuario"
                  className="admin-form-select"
                  value={nuevoUsuario.typeUser}
                  onChange={(e) => setNuevoUsuario({ ...nuevoUsuario, typeUser: parseInt(e.target.value) })}
                >
                  <option value="0">Usuario Normal</option>
                  <option value="100">Usuario Gas</option>
                  <option value="485">Administrador</option>
                </select>
              </div>

              <div className="admin-form-group">
                <label htmlFor="nuevo-usuario" className="admin-form-label">Usuario</label>
                <input
                  id="nuevo-usuario"
                  className="admin-form-input"
                  type="text"
                  value={nuevoUsuario.usuario}
                  onChange={(e) => setNuevoUsuario({ ...nuevoUsuario, usuario: e.target.value })}
                  autoComplete="new-username"
                />
              </div>

              <div className="admin-form-group">
                <label htmlFor="nuevo-password" className="admin-form-label">Contraseña (máx 8)</label>
                <input
                  id="nuevo-password"
                  className="admin-form-input"
                  type="password"
                  maxLength="8"
                  value={nuevoUsuario.password}
                  onChange={(e) => setNuevoUsuario({ ...nuevoUsuario, password: e.target.value })}
                  autoComplete="new-password"
                />
              </div>

              <button className="admin-form-button" onClick={agregarUsuario}>Agregar Usuario</button>
            </div>

            <div className="usuarios-list-section">
              <div className="usuarios-toolbar">
                <label className="usuarios-toolbar-toggle">
                  <input
                    type="checkbox"
                    checked={mostrarInactivos}
                    onChange={(e) => setMostrarInactivos(e.target.checked)}
                  />
                  <span>Mostrar usuarios inactivos</span>
                </label>
                {mostrarInactivos && (
                  <span className="usuarios-toolbar-count">
                    · {usuarios.filter(u => u.activo === 0).length} inactivo(s)
                  </span>
                )}
                <span className="usuarios-toolbar-spacer" />
                <span className="usuarios-toolbar-total">
                  {usuariosActivos.length} activo(s)
                </span>
              </div>

              <div className="usuarios-table-container">
            <table className="usuarios-table">
              <thead>
                <tr>
                  <th>Nombre</th>
                  <th>Apellidos</th>
                  <th>Usuario</th>
                  <th>Teléfono</th>
                  <th>WhatsApp</th>
                  <th>Horario Inicio</th>
                  <th>Horario Fin</th>
                  <th>Tipo</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {usuarios.map((u) => (
                  <tr key={u.id} style={u.activo === 0 ? { opacity: 0.55, background: '#f4f4f4', textDecoration: 'line-through' } : undefined}>
                    {usuarioEditando && usuarioEditando.id === u.id ? (
                      <>
                        <td>
                          <input
                            type="text"
                            value={usuarioEditando.nombre}
                            onChange={(e) => setUsuarioEditando({ ...usuarioEditando, nombre: e.target.value })}
                          />
                        </td>
                        <td>
                          <input
                            type="text"
                            value={usuarioEditando.apellidos}
                            onChange={(e) => setUsuarioEditando({ ...usuarioEditando, apellidos: e.target.value })}
                          />
                        </td>
                        <td>
                          <input
                            type="text"
                            value={usuarioEditando.usuario}
                            onChange={(e) => setUsuarioEditando({ ...usuarioEditando, usuario: e.target.value })}
                          />
                        </td>
                        <td>
                          <input
                            type="tel"
                            value={usuarioEditando.telefono}
                            onChange={(e) => setUsuarioEditando({ ...usuarioEditando, telefono: e.target.value })}
                          />
                        </td>
                        <td>-</td>
                        <td>
                          <input
                            type="time"
                            value={usuarioEditando.horario_inicio}
                            onChange={(e) => setUsuarioEditando({ ...usuarioEditando, horario_inicio: e.target.value })}
                          />
                        </td>
                        <td>
                          <input
                            type="time"
                            value={usuarioEditando.horario_fin}
                            onChange={(e) => setUsuarioEditando({ ...usuarioEditando, horario_fin: e.target.value })}
                          />
                        </td>
                        <td>
                          <select
                            value={usuarioEditando.typeUser}
                            onChange={(e) => setUsuarioEditando({ ...usuarioEditando, typeUser: parseInt(e.target.value) })}
                          >
                            <option value="0">Usuario Normal</option>
                            <option value="100">Usuario Gas</option>
                            <option value="485">Administrador</option>
                          </select>
                        </td>
                        <td>
                          <button onClick={() => actualizarUsuario(u.id)}>Guardar</button>
                          <button onClick={() => setUsuarioEditando(null)}>Cancelar</button>
                        </td>
                      </>
                    ) : (
                      <>
                        <td>{u.nombre}</td>
                        <td>{u.apellidos}</td>
                        <td>{u.usuario}</td>
                        <td>{u.telefono}</td>
                        <td>
                          {u.telefono && (
                            <a
                              href={`https://wa.me/+34${u.telefono.replace(/\D/g, '')}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="whatsapp-link"
                              title="Abrir chat de WhatsApp"
                            >
                              <span role="img" aria-label="whatsapp">💬</span>
                            </a>
                          )}
                        </td>
                        <td>{u.horario_inicio}</td>
                        <td>{u.horario_fin}</td>
                        <td>{u.typeUser === 485 ? 'Administrador' : u.typeUser === 100 ? 'Usuario Gas' : 'Usuario Normal'}</td>
                        <td>
                          <button onClick={() => setUsuarioEditando({ ...u })}>Editar</button>
                          {u.activo === 0 ? (
                            <button onClick={() => reactivarUsuario(u.id)} style={{ background:'#16a34a', color:'#fff' }}>Reactivar</button>
                          ) : (
                            <button onClick={() => desactivarUsuario(u.id, u.nombre_completo)}>Desactivar</button>
                          )}
                          <button
                            onClick={() => copiarMensaje(u)}
                            className="copy-message-btn"
                            title="Copiar mensaje de bienvenida"
                          >
                            <span role="img" aria-label="copy">📋</span>
                          </button>
                        </td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
              </div>
            </div>
          </div>
        )}
        {activeTab === 'ausencias' && esAdmin && (
          <div className="fichajes-panel">
            {/* ── Formulario crear / editar ── */}
            <div className="admin-new-user-form" style={{ marginBottom: '24px' }}>
              <h3>{ausenciaEditando ? 'Editar ausencia' : 'Registrar ausencia'}</h3>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'flex-end', marginTop: '12px' }}>
                <div className="admin-form-group">
                  <label className="admin-form-label">Empleado *</label>
                  <select className="admin-form-input"
                    value={ausenciaForm.usuario}
                    onChange={e => setAusenciaForm(f => ({ ...f, usuario: e.target.value }))}>
                    <option value="">Seleccionar…</option>
                    {usuariosActivos.map(u => (
                      <option key={u.id} value={u.usuario}>
                        {u.nombre_completo || `${u.nombre} ${u.apellidos}`}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="admin-form-group">
                  <label className="admin-form-label">Desde *</label>
                  <input className="admin-form-input" type="date"
                    value={ausenciaForm.fecha_inicio}
                    onChange={e => setAusenciaForm(f => ({
                      ...f,
                      fecha_inicio: e.target.value,
                      // si parcial, fecha_fin se sincroniza
                      fecha_fin: f.parcial ? e.target.value : f.fecha_fin,
                    }))} />
                </div>
                <div className="admin-form-group">
                  <label className="admin-form-label">
                    Hasta {ausenciaForm.parcial && <span style={{ color: '#888', fontSize: '.75rem' }}>(= Desde)</span>} *
                  </label>
                  <input className="admin-form-input" type="date"
                    disabled={ausenciaForm.parcial}
                    value={ausenciaForm.fecha_fin}
                    onChange={e => setAusenciaForm(f => ({ ...f, fecha_fin: e.target.value }))} />
                </div>
                <div className="admin-form-group">
                  <label className="admin-form-label">Tipo *</label>
                  <select className="admin-form-input"
                    value={ausenciaForm.tipo}
                    onChange={e => setAusenciaForm(f => ({ ...f, tipo: e.target.value }))}>
                    <option value="vacacion">Vacación</option>
                    <option value="libre">Día libre</option>
                    <option value="baja">Baja</option>
                    <option value="cita_medica">Cita médica</option>
                    <option value="otro">Otro</option>
                  </select>
                </div>
                <div className="admin-form-group" style={{ flex: '1', minWidth: '200px' }}>
                  <label className="admin-form-label">Motivo</label>
                  <input className="admin-form-input" type="text" placeholder="Opcional"
                    value={ausenciaForm.motivo}
                    onChange={e => setAusenciaForm(f => ({ ...f, motivo: e.target.value }))} />
                </div>

                {/* ── Ausencia parcial (mismo día, rango horario) ── */}
                <div className="admin-form-group" style={{ flexBasis: '100%' }}>
                  <label style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontSize: '.9rem', color: '#333', fontWeight: 500 }}>
                    <input type="checkbox"
                      checked={ausenciaForm.parcial}
                      onChange={e => {
                        const parcial = e.target.checked;
                        setAusenciaForm(f => ({
                          ...f,
                          parcial,
                          // si marca parcial, forzar fecha_fin = fecha_inicio
                          fecha_fin: parcial && f.fecha_inicio ? f.fecha_inicio : f.fecha_fin,
                          // si desmarca, limpiar horas
                          hora_inicio: parcial ? f.hora_inicio : '',
                          hora_fin:    parcial ? f.hora_fin    : '',
                        }));
                      }} />
                    Ausencia parcial (rango de horas, mismo día)
                  </label>
                </div>
                {ausenciaForm.parcial && (
                  <>
                    <div className="admin-form-group">
                      <label className="admin-form-label">Desde hora *</label>
                      <input className="admin-form-input" type="time"
                        value={ausenciaForm.hora_inicio}
                        onChange={e => setAusenciaForm(f => ({ ...f, hora_inicio: e.target.value }))} />
                    </div>
                    <div className="admin-form-group">
                      <label className="admin-form-label">Hasta hora *</label>
                      <input className="admin-form-input" type="time"
                        value={ausenciaForm.hora_fin}
                        onChange={e => setAusenciaForm(f => ({ ...f, hora_fin: e.target.value }))} />
                    </div>
                  </>
                )}

                <div style={{ display: 'flex', gap: '8px' }}>
                  <button className="export-btn" onClick={guardarAusencia} disabled={ausenciaGuardando}
                    style={{ backgroundColor: '#27ae60' }}>
                    {ausenciaGuardando ? 'Guardando…' : (ausenciaEditando ? '💾 Actualizar' : '+ Registrar')}
                  </button>
                  {ausenciaEditando && (
                    <button className="export-btn" style={{ backgroundColor: '#6c757d' }}
                      onClick={() => { setAusenciaEditando(null); setAusenciaForm({ usuario:'', fecha_inicio:'', fecha_fin:'', tipo:'vacacion', motivo:'', parcial:false, hora_inicio:'', hora_fin:'' }); }}>
                      Cancelar
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* ── Filtro + tabla ── */}
            <div className="toolbar" style={{ marginBottom: '12px' }}>
              <select value={ausenciaFiltroUsuario}
                onChange={e => { setAusenciaFiltroUsuario(e.target.value); cargarAusencias(e.target.value); }}
                style={{ padding: '8px 12px', border: '1px solid #ccc', borderRadius: '8px', minWidth: '200px' }}>
                <option value="">Todos los empleados</option>
                {usuariosActivos.map(u => (
                  <option key={u.id} value={u.usuario}>
                    {u.nombre_completo || `${u.nombre} ${u.apellidos}`}
                  </option>
                ))}
              </select>
              <span style={{ color: '#888', fontSize: '.85rem' }}>
                {ausencias.length} registro{ausencias.length !== 1 ? 's' : ''}
              </span>
            </div>

            {ausencias.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#888' }}>
                No hay ausencias registradas.
              </div>
            ) : (
              <table className="fichajes-table">
                <thead>
                  <tr>
                    <th>Empleado</th>
                    <th>Desde</th>
                    <th>Hasta</th>
                    <th>Días</th>
                    <th>Horario</th>
                    <th>Tipo</th>
                    <th>Motivo</th>
                    <th>Registrado por</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {ausencias.map(a => {
                    const tipoLabel = { vacacion:'Vacación', libre:'Día libre', baja:'Baja', cita_medica:'Cita médica', otro:'Otro' }[a.tipo] || a.tipo;
                    const tipoColor = { vacacion:'#4e58f2', libre:'#27ae60', baja:'#e74c3c', cita_medica:'#e67e22', otro:'#888' }[a.tipo] || '#888';
                    const esParcial = !!(a.hora_inicio && a.hora_fin);
                    const dias = esParcial ? 1 : Math.round((new Date(a.fecha_fin) - new Date(a.fecha_inicio)) / 86400000) + 1;
                    return (
                      <tr key={a.id}>
                        <td><b>{a.usuario}</b></td>
                        <td>{a.fecha_inicio}</td>
                        <td>{a.fecha_fin}</td>
                        <td style={{ textAlign: 'center' }}>{esParcial ? '½' : dias}</td>
                        <td style={{ fontSize: '.85rem' }}>
                          {esParcial
                            ? <span style={{ color: '#e67e22', fontWeight: 600 }}>{a.hora_inicio} → {a.hora_fin}</span>
                            : <span style={{ color: '#888' }}>Día completo</span>}
                        </td>
                        <td><span style={{ background: tipoColor + '22', color: tipoColor, padding: '2px 10px', borderRadius: '12px', fontSize: '.78rem', fontWeight: 600 }}>{tipoLabel}</span></td>
                        <td style={{ color: '#555', fontSize: '.85rem' }}>{a.motivo || '—'}</td>
                        <td style={{ color: '#888', fontSize: '.82rem' }}>{a.creado_por || '—'}</td>
                        <td>
                          <div style={{ display: 'flex', gap: '6px', justifyContent: 'center' }}>
                            <button className="export-btn" style={{ padding: '4px 10px', fontSize: '.78rem', backgroundColor: '#e67e22' }}
                              onClick={() => editarAusencia(a)}>✏️</button>
                            <button className="export-btn" style={{ padding: '4px 10px', fontSize: '.78rem', backgroundColor: '#e74c3c' }}
                              onClick={() => eliminarAusencia(a.id)}>🗑️</button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminPanel;
